<template>
  <section class="d-grid gap-4">
    <header>
      <h2 class="h4 mb-1">Mensajes y avisos</h2>
      <p class="text-muted mb-0">
        Textos que se envían al cliente por WhatsApp y los tiempos de entrega que aparecen en ellos.
      </p>
    </header>

    <div v-if="!canManageEcommerce" class="alert alert-warning mb-0">
      No tenés permiso para configurar el ecommerce.
    </div>

    <template v-else>
      <div v-if="loadError" class="alert alert-danger mb-0">{{ loadError }}</div>

      <div class="card shadow-sm">
        <div class="card-body">
          <h3 class="h6 mb-3">Tiempos de entrega</h3>
          <div class="row g-3">
            <div class="col-md-4">
              <label class="form-label" for="envio-horas">Demora de envío (horas)</label>
              <input
                id="envio-horas"
                v-model.number="form.envio_demora_horas"
                type="number"
                min="0"
                :max="MAX_HORAS"
                class="form-control"
              />
              <div class="form-text">Reemplaza a <code>{horas}</code> en el aviso de envío.</div>
            </div>
            <div class="col-md-4">
              <label class="form-label" for="retiro-horas">Demora de retiro (horas)</label>
              <input
                id="retiro-horas"
                v-model.number="form.retiro_demora_horas"
                type="number"
                min="0"
                :max="MAX_HORAS"
                class="form-control"
              />
              <div class="form-text">Reemplaza a <code>{horas}</code> en el aviso de retiro.</div>
            </div>
          </div>
        </div>
      </div>

      <div v-for="campo in plantillas" :key="campo.key" class="card shadow-sm">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-start gap-3 mb-2">
            <div>
              <h3 class="h6 mb-1">{{ campo.label }}</h3>
              <p class="text-muted small mb-0">{{ campo.ayuda }}</p>
            </div>
            <span v-if="esDefault(campo.key)" class="badge text-bg-secondary flex-shrink-0">Texto por defecto</span>
            <button
              v-else
              type="button"
              class="btn btn-outline-secondary btn-sm flex-shrink-0"
              @click="restaurarDefault(campo.key)"
            >
              Restaurar por defecto
            </button>
          </div>

          <textarea
            v-model="form[campo.key]"
            class="form-control font-monospace"
            rows="7"
            :maxlength="MAX_LENGTH"
          ></textarea>
          <div class="d-flex justify-content-between mt-1">
            <small class="text-muted">
              Variables:
              <code v-for="v in variables" :key="v" class="me-1">{{ '{' + v + '}' }}</code>
            </small>
            <small :class="['text-muted', (form[campo.key] || '').length > MAX_LENGTH - 50 ? 'text-danger' : '']">
              {{ (form[campo.key] || '').length }} / {{ MAX_LENGTH }}
            </small>
          </div>

          <div class="mt-3">
            <label class="form-label small text-muted mb-1">Vista previa (con datos de ejemplo)</label>
            <pre class="preview mb-0">{{ vistaPrevia(campo.key) }}</pre>
          </div>
        </div>
      </div>

      <div class="d-flex gap-2 align-items-center">
        <button type="button" class="btn btn-dark" :disabled="saving" @click="guardar">
          {{ saving ? 'Guardando…' : 'Guardar cambios' }}
        </button>
        <span v-if="saveMessage" :class="saveOk ? 'text-success' : 'text-danger'">{{ saveMessage }}</span>
      </div>
    </template>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { hasPermission } from '@/services/authSessionService'
import ecommerceConfigService from '@/services/ecommerceConfigService'

// RF-3 de SPEC_CONSULTORIA_negocio_v0.1.md (ecommerce-graciela/docs).
//
// Los defaults, las variables disponibles y el largo máximo NO se
// hardcodean acá: los devuelve el backend en `avisos_default`,
// `avisos_variables` y `avisos_max_length`. Si el default cambia en el
// servidor, este panel lo refleja sin tocar el frontend — evita el clásico
// desfasaje entre lo que el panel dice que es el default y lo que el
// backend realmente aplica.

const canManageEcommerce = computed(() => hasPermission('gestionar_ecommerce'))

const plantillas = [
  {
    key: 'msg_resumen_compra',
    label: 'Resumen de compra',
    ayuda: 'Se envía en caja, después de cobrar, en lugar de imprimir la factura.',
  },
  {
    key: 'msg_listo_retiro',
    label: 'Aviso de retiro en el local',
    ayuda: 'Se envía antes de cobrar, cuando el pedido es para retirar y ya está preparado.',
  },
  {
    key: 'msg_preparando_envio',
    label: 'Aviso de preparación de envío',
    ayuda: 'Se envía antes de cobrar, cuando el pedido es para envío a domicilio.',
  },
]

const form = reactive({
  msg_resumen_compra: '',
  msg_listo_retiro: '',
  msg_preparando_envio: '',
  envio_demora_horas: null,
  retiro_demora_horas: null,
})

const defaults = ref({})
const variables = ref([])
const MAX_LENGTH = ref(1000)
const MAX_HORAS = 720

const saving = ref(false)
const saveMessage = ref('')
const saveOk = ref(false)
const loadError = ref('')

// Datos de ejemplo para la vista previa. Deliberadamente concretos y
// realistas: un placeholder tipo "XXX" no deja ver si el mensaje se lee
// bien de verdad.
const EJEMPLO = {
  cliente: 'María González',
  pedido_id: '1042',
  total: 'Gs. 185.000',
  detalle: '- 2 x Proteína Whey 1kg (Gs. 75.000 c/u)\n- 1 x Colágeno hidrolizado (Gs. 35.000)',
  comercio: 'Consultorio Nutricional',
  direccion: 'Av. España 1234 c/ Brasil',
  fecha_entrega: '08/08/2026',
}

function aplicarVariables(texto, horas) {
  const valores = { ...EJEMPLO, horas: horas ?? '' }
  return String(texto || '').replace(/\{(\w+)\}/g, (literal, nombre) => (
    // Variable desconocida: se deja literal, no se rompe el mensaje.
    Object.prototype.hasOwnProperty.call(valores, nombre) ? String(valores[nombre] ?? '') : literal
  ))
}

function vistaPrevia(key) {
  const horas = key === 'msg_preparando_envio' ? form.envio_demora_horas : form.retiro_demora_horas
  return aplicarVariables(form[key], horas ?? defaults.value[key === 'msg_preparando_envio' ? 'envio_demora_horas' : 'retiro_demora_horas'])
}

const esDefault = (key) => (form[key] || '') === (defaults.value[key] || '')

function restaurarDefault(key) {
  form[key] = defaults.value[key] || ''
}

async function load() {
  if (!canManageEcommerce.value) return
  try {
    const data = await ecommerceConfigService.get()
    defaults.value = data.avisos_default || {}
    variables.value = data.avisos_variables || []
    MAX_LENGTH.value = data.avisos_max_length || 1000
    // Se cargan los valores EFECTIVOS (lo configurado, o el default si está
    // en NULL) para que el textarea muestre el texto que realmente se envía
    // hoy, no un campo vacío que el operador no sabría cómo llenar.
    const efectivos = data.avisos_efectivos || {}
    for (const { key } of plantillas) form[key] = efectivos[key] || ''
    form.envio_demora_horas = efectivos.envio_demora_horas ?? null
    form.retiro_demora_horas = efectivos.retiro_demora_horas ?? null
  } catch (error) {
    loadError.value = error?.response?.data?.error || 'No se pudo cargar la configuración de mensajes.'
  }
}

async function guardar() {
  saving.value = true
  saveMessage.value = ''
  try {
    // Un campo que quedó igual al default se guarda como `null` en vez de
    // como override: así el comercio sigue heredando futuros cambios del
    // default. Si guardáramos el texto tal cual, cada guardado congelaría
    // el default vigente como si el usuario lo hubiera escrito.
    const payload = {}
    for (const { key } of plantillas) {
      payload[key] = esDefault(key) ? null : form[key]
    }
    for (const key of ['envio_demora_horas', 'retiro_demora_horas']) {
      payload[key] = form[key] === defaults.value[key] ? null : form[key]
    }
    await ecommerceConfigService.update(payload)
    saveOk.value = true
    saveMessage.value = 'Cambios guardados.'
    await load()
  } catch (error) {
    saveOk.value = false
    saveMessage.value = error?.response?.data?.error || 'No se pudieron guardar los cambios.'
  } finally {
    saving.value = false
  }
}

onMounted(load)
</script>

<style scoped>
.preview {
  background: #e7f3e8;
  border: 1px solid #cfe3d0;
  border-radius: .5rem;
  padding: .75rem 1rem;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: .875rem;
  color: #111;
}
</style>
